import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MessageSquareHeart, Apple, UserPlus } from "lucide-react";
import { routes } from "@/lib/routes";
import mateoQrCode from "@/assets/mateo-qrcode.png";
import mateoAndroidQrCode from "@/assets/mateo-android-qrcode.png";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TrustBadges } from "@/components/TrustBadges";
import { ReferCoachDialog } from "@/components/referral/ReferCoachDialog";

export const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [referDialogOpen, setReferDialogOpen] = useState(false);
  const { t } = useTranslation();

  return (
    <footer className="bg-muted/30 border-t border-border mt-auto pb-20 md:pb-0">
      <div className="container mx-auto px-4 py-6">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6 mb-4">
          <div>
            <h3 className="font-semibold text-foreground mb-2 text-sm md:text-base">{t('footer.about')}</h3>
            <ul className="space-y-1 text-sm text-muted-foreground">
              <li><Link to={routes.aboutUs()} className="hover:text-primary transition-colors py-0.5 inline-block">{t('footer.aboutUs')}</Link></li>
              <li><Link to={routes.howItWorks()} className="hover:text-primary transition-colors py-0.5 inline-block">{t('footer.howItWorks')}</Link></li>
              <li><Link to="/blog" className="hover:text-primary transition-colors py-0.5 inline-block">Blog</Link></li>
              <li><Link to={routes.termsOfService()} className="hover:text-primary transition-colors py-0.5 inline-block">{t('footer.termsOfService')}</Link></li>
            </ul>
          </div>

          <div>
            <h3 className="font-semibold text-foreground mb-2 text-sm md:text-base">{t('footer.forCoaches')}</h3>
            <ul className="space-y-1 text-sm text-muted-foreground">
              <li><Link to={routes.becomeCoach()} className="hover:text-primary transition-colors py-0.5 inline-block">{t('footer.becomeCoach')}</Link></li>
              <li><Link to={routes.becomeMaestro()} className="hover:text-primary transition-colors py-0.5 inline-block">{t('footer.becomeMaestro')}</Link></li>
              <li><Link to={routes.coachAcademy()} className="hover:text-primary transition-colors py-0.5 inline-block">{t('footer.coachAcademy')}</Link></li>
              <li><Link to={routes.academyApply()} className="hover:text-primary transition-colors py-0.5 inline-block">{t('footer.joinAcademy')}</Link></li>
            </ul>
          </div>

          <div>
            <h3 className="font-semibold text-foreground mb-2 text-sm md:text-base">
              <Link to={routes.becomeVenuePartner()} className="hover:text-primary transition-colors">{t('footer.partnerPrograms')}</Link>
            </h3>
            <ul className="space-y-1 text-sm text-muted-foreground pl-3 border-l border-border/50">
              <li><Link to={routes.becomeReferralPartner()} className="hover:text-primary transition-colors py-0.5 inline-block">{t('footer.affiliateProgram')}</Link></li>
              <li><Link to="/partner/customer-academy#hero" className="hover:text-primary transition-colors py-0.5 inline-block">{t('footer.businessFoundry')}</Link></li>
              <li><Link to="/mateo-open" className="hover:text-primary transition-colors py-0.5 inline-block">{t('footer.mateoOpen')}</Link></li>
              <li><Link to={routes.mateoStage()} className="hover:text-primary transition-colors py-0.5 inline-block">{t('footer.mateoStage')}</Link></li>
            </ul>
          </div>

          <div>
            <h3 className="font-semibold text-foreground mb-2 text-sm md:text-base">{t('footer.support')}</h3>
            <ul className="space-y-1 text-sm text-muted-foreground">
              <li><Link to={routes.helpCenter()} className="hover:text-primary transition-colors py-0.5 inline-block">{t('nav.faq')}</Link></li>
              <li><Link to={routes.contactUs()} className="hover:text-primary transition-colors py-0.5 inline-block">{t('footer.contactUs')}</Link></li>
              <li>
                <Link to="/feedback" className="hover:text-primary transition-colors py-0.5 inline-flex items-center gap-1.5">
                  <MessageSquareHeart className="h-3.5 w-3.5" />
                  {t('footer.feedback', 'Feedback')}
                </Link>
              </li>
              <li>
                <button onClick={() => setReferDialogOpen(true)} className="hover:text-primary transition-colors py-0.5 inline-flex items-center gap-1.5">
                  <UserPlus className="h-3.5 w-3.5" />
                  {t('footer.referCoach')}
                </button>
              </li>
              <li>
                <Popover>
                  <PopoverTrigger className="hover:text-primary transition-colors py-0.5 inline-flex items-center gap-1.5 cursor-pointer">
                    <Apple className="h-3.5 w-3.5" />
                    {t('footer.getTheApp')}
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-4" align="start">
                    <div className="flex flex-col items-center gap-3">
                      <p className="text-sm font-medium text-foreground">{t('footer.scanToDownload')}</p>
                      <Tabs defaultValue="ios" className="w-full">
                        <TabsList className="grid w-full grid-cols-2">
                          <TabsTrigger value="ios" className="text-xs">iOS</TabsTrigger>
                          <TabsTrigger value="android" className="text-xs">Android</TabsTrigger>
                        </TabsList>
                        <TabsContent value="ios" className="flex flex-col items-center gap-3 mt-3">
                          <img src={mateoQrCode} alt="Download Mateo iOS App QR Code" className="w-32 h-32 rounded-lg border" />
                          <a href="https://apps.apple.com/ca/app/mateo-skills-on-your-terms/id6754468546" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors">
                            <Apple className="h-4 w-4" />
                            App Store
                          </a>
                        </TabsContent>
                        <TabsContent value="android" className="flex flex-col items-center gap-3 mt-3">
                          <img src={mateoAndroidQrCode} alt="Download Mateo Android App QR Code" className="w-32 h-32 rounded-lg border" />
                          <a
                            href="https://appbuild.diy/preview/0296ed91-44ef-4dbd-b566-553e9d3bd3a8"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors w-full justify-center"
                          >
                            Open Android Download Link
                          </a>
                          <button
                            type="button"
                            onClick={() => {
                              navigator.clipboard.writeText("https://appbuild.diy/preview/0296ed91-44ef-4dbd-b566-553e9d3bd3a8");
                            }}
                            className="text-xs text-muted-foreground hover:text-primary transition-colors underline"
                          >
                            Copy link
                          </button>
                        </TabsContent>
                      </Tabs>
                    </div>
                  </PopoverContent>
                </Popover>
              </li>
            </ul>
          </div>

          <div className="col-span-2 md:col-span-1">
            <h3 className="font-semibold text-foreground mb-2 text-sm md:text-base">{t('footer.legal')}</h3>
            <ul className="space-y-1 text-sm text-muted-foreground">
              <li><Link to={routes.privacyPolicy()} className="hover:text-primary transition-colors py-0.5 inline-block">{t('footer.privacyPolicy')}</Link></li>
              <li><Link to={routes.waiverOfLiability()} className="hover:text-primary transition-colors py-0.5 inline-block">{t('footer.waiverOfLiability')}</Link></li>
              <li><Link to={routes.highRiskAddendum()} className="hover:text-primary transition-colors py-0.5 inline-block">High-Risk Activity Release</Link></li>
              <li><Link to={routes.providerAgreement()} className="hover:text-primary transition-colors py-0.5 inline-block">{t('footer.providerAgreement')}</Link></li>
            </ul>
          </div>
        </div>

        <div className="py-6 border-t border-border bg-gradient-to-r from-primary/5 via-transparent to-primary/5 rounded-lg px-6 text-center mb-4">
          <p className="text-lg font-semibold text-foreground mb-1">Boredom is a choice. Mastery is a tap away.</p>
          <Link to={routes.browseExperiences()} className="text-primary hover:text-primary/80 font-medium text-sm transition-colors">
            Find an Experience Near You →
          </Link>
        </div>

        <div className="py-4 border-t border-border">
          <TrustBadges variant="full" showLovable={false} className="flex-wrap" />
        </div>

        <div className="pt-4 border-t border-border text-center text-sm text-muted-foreground">
          <p>{t('footer.copyright', { year: currentYear })}</p>
        </div>
      </div>

      <ReferCoachDialog open={referDialogOpen} onOpenChange={setReferDialogOpen} />
    </footer>
  );
};
